import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const routes = [
    {
        path: '/',
        redirect: '/login'
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: { guest: true }
    },
    {
        path: '/password/reset',
        name: 'password.request',
        component: () => import('../pages/auth/ForgotPassword.vue'),
        meta: { guest: true }
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('../pages/auth/Register.vue'),
        meta: { guest: true }
    },
    {
        path: '/admin',
        component: () => import('../layouts/AdminLayout.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'super_admin', 'provider'] },
        children: [
            {
                path: 'dashboard',
                name: 'admin.dashboard',
                component: () => import('../pages/admin/dashboard/Index.vue')
            },
            {
                path: 'dashboard/scheduled-services',
                name: 'admin.dashboard.scheduled-services',
                component: () => import('../pages/admin/dashboard/ScheduledServicesCalendar.vue')
            },
            // Services Module
            {
                path: 'services',
                name: 'admin.services.index',
                component: () => import('../pages/admin/services/Index.vue'),
                meta: { permission: 'manage_services' }
            },
            {
                path: 'services/create',
                name: 'admin.services.create',
                component: () => import('../pages/admin/services/Create.vue'),
                meta: { permission: 'manage_services' }
            },
            {
                path: 'services/:id/edit',
                name: 'admin.services.edit',
                component: () => import('../pages/admin/services/Edit.vue'),
                meta: { permission: 'manage_services' }
            },
            // Customers Module
            {
                path: 'customers',
                name: 'admin.customers.index',
                component: () => import('../pages/admin/customers/Index.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'customers/create',
                name: 'admin.customers.create',
                component: () => import('../pages/admin/customers/Create.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'customers/:id/edit',
                name: 'admin.customers.edit',
                component: () => import('../pages/admin/customers/Edit.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'customers/:id/properties',
                name: 'admin.customers.properties',
                component: () => import('../pages/admin/customers/Properties.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'customers/:id/scheduled-services',
                name: 'admin.customers.scheduled-services',
                component: () => import('../pages/admin/customers/ScheduledServices.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'customers/:id/order-history',
                name: 'admin.customers.order-history',
                component: () => import('../pages/admin/customers/OrderHistory.vue'),
                meta: { permission: 'manage_customers' }
            },
            // Properties Module
            {
                path: 'properties',
                name: 'admin.properties.index',
                component: () => import('../pages/admin/properties/Index.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'properties/:id/edit',
                name: 'admin.properties.edit',
                component: () => import('../pages/admin/properties/Edit.vue'),
                meta: { permission: 'manage_customers' }
            },
            // Scheduled Services — under property
            {
                path: 'properties/:id/scheduled-services',
                name: 'admin.properties.scheduled-services',
                component: () => import('../pages/admin/scheduled-services/Index.vue'),
                meta: { permission: 'manage_customers' }
            },
            {
                path: 'properties/:id/order-history',
                name: 'admin.properties.order-history',
                component: () => import('../pages/admin/properties/OrderHistory.vue'),
                meta: { permission: 'manage_customers' }
            },
            // Scheduled Services — under customer > property
            {
                path: 'customers/:customerId/properties/:propertyId/scheduled-services',
                name: 'admin.customers.properties.scheduled-services',
                component: () => import('../pages/admin/scheduled-services/Index.vue'),
                meta: { permission: 'manage_customers' }
            },
            // Scheduled Services Module (standalone)
            {
                path: 'scheduled-services',
                name: 'admin.scheduled-services.index',
                component: () => import('../pages/admin/scheduled-services/Index.vue'),
                meta: { permission: 'manage_customers' }
            },
            // Orders Module
            {
                path: 'orders',
                name: 'admin.orders.index',
                component: () => import('../pages/admin/orders/Index.vue'),
                meta: { permission: 'view_orders' }
            },
            {
                path: 'orders/create',
                name: 'admin.orders.create',
                component: () => import('../pages/admin/orders/Create.vue'),
                meta: { permission: 'manage_orders' }
            },
            {
                path: 'orders/upload-work-order',
                name: 'admin.orders.upload-work-order',
                component: () => import('../pages/admin/orders/Create.vue'),
                meta: { permission: 'manage_orders' }
            },
            {
                path: 'orders/history',
                name: 'admin.orders.history',
                component: () => import('../pages/admin/orders/History.vue'),
                meta: { permission: 'manage_orders' }
            },
            {
                path: 'orders/bulk-assign',
                name: 'admin.orders.bulk-assign',
                component: () => import('../pages/admin/orders/BulkAssign.vue'),
                meta: { permission: 'manage_orders' }
            },
            {
                path: 'orders/:orderNumber/invoice',
                name: 'admin.orders.invoice',
                component: () => import('../pages/admin/orders/Invoice.vue'),
                meta: { permission: 'manage_orders' }
            },
            {
                path: 'orders/:orderNumber',
                name: 'admin.orders.show',
                component: () => import('../pages/admin/orders/Show.vue'),
                meta: { permission: 'manage_orders' }
            },
            // Tasks Module
            {
                path: 'tasks',
                name: 'admin.tasks.index',
                component: () => import('../pages/admin/orders/Tasks.vue'),
                meta: { permission: 'view_tasks' }
            },
            // Vendors Module
            {
                path: 'vendors',
                name: 'admin.vendors.index',
                component: () => import('../pages/admin/vendors/Index.vue'),
                meta: { permission: 'manage_vendors' }
            },
            {
                path: 'vendors/create',
                name: 'admin.vendors.create',
                component: () => import('../pages/admin/vendors/Create.vue'),
                meta: { permission: 'manage_vendors' }
            },
            {
                path: 'vendors/tasks',
                name: 'admin.vendors.tasks',
                component: () => import('../pages/admin/vendors/Tasks.vue'),
                meta: { permission: 'manage_vendors' }
            },
            {
                path: 'vendors/:id/edit',
                name: 'admin.vendors.edit',
                component: () => import('../pages/admin/vendors/Edit.vue'),
                meta: { permission: 'manage_vendors' }
            },
            // Staff Module
            {
                path: 'staff',
                name: 'admin.staff.index',
                component: () => import('../pages/admin/staff/Index.vue'),
                meta: { permission: 'view_staff' }
            },
            {
                path: 'staff/create',
                name: 'admin.staff.create',
                component: () => import('../pages/admin/staff/Create.vue'),
                meta: { permission: 'manage_staff' }
            },
            {
                path: 'staff/schedules',
                name: 'admin.staff.schedules',
                component: () => import('../pages/admin/staff/Schedules.vue'),
                meta: { permission: 'view_staff' }
            },
            {
                path: 'staff/leave-requests',
                name: 'admin.staff.leave-requests',
                component: () => import('../pages/admin/staff/LeaveRequests.vue'),
                meta: { permission: 'view_staff' }
            },
            {
                path: 'staff/:id/edit',
                name: 'admin.staff.edit',
                component: () => import('../pages/admin/staff/Edit.vue'),
                meta: { permission: 'manage_staff' }
            },
            // Appointments Module
            {
                path: 'appointments',
                name: 'admin.appointments.index',
                component: () => import('../pages/admin/appointments/Index.vue')
            },
            {
                path: 'appointments/create',
                name: 'admin.appointments.create',
                component: () => import('../pages/admin/appointments/Create.vue')
            },
            {
                path: 'appointments/:id/edit',
                name: 'admin.appointments.edit',
                component: () => import('../pages/admin/appointments/Edit.vue')
            },
            // Quotations Module
            {
                path: 'quotations',
                name: 'admin.quotations.index',
                component: () => import('../pages/admin/quotations/Index.vue'),
                meta: { permission: 'view_quotations' }
            },
            {
                path: 'quotations/create',
                name: 'admin.quotations.create',
                component: () => import('../pages/admin/quotations/Create.vue'),
                meta: { permission: 'manage_quotations' }
            },
            {
                path: 'quotations/:id/edit',
                name: 'admin.quotations.edit',
                component: () => import('../pages/admin/quotations/Edit.vue'),
                meta: { permission: 'manage_quotations' }
            },
            // Quick Quotations Module
            {
                path: 'quick-quotations',
                name: 'admin.quick-quotations.index',
                component: () => import('../pages/admin/quotations/QuickQuotations.vue'),
                meta: { permission: 'view_quotations' }
            },
            {
                path: 'quick-quotations/create',
                name: 'admin.quick-quotations.create',
                component: () => import('../pages/admin/quotations/QuickQuotationCreate.vue'),
                meta: { permission: 'manage_quotations' }
            },
            {
                path: 'quick-quotations/:id/edit',
                name: 'admin.quick-quotations.edit',
                component: () => import('../pages/admin/quotations/QuickQuotationEdit.vue'),
                meta: { permission: 'manage_quotations' }
            },
            // Quotation Templates Module
            {
                path: 'quotation-templates',
                name: 'admin.quotation-templates.index',
                component: () => import('../pages/admin/quotations/Templates.vue'),
                meta: { permission: 'manage_quotations' }
            },
            {
                path: 'quotation-templates/create',
                name: 'admin.quotation-templates.create',
                component: () => import('../pages/admin/quotations/TemplateCreate.vue'),
                meta: { permission: 'manage_quotations' }
            },
            {
                path: 'quotation-templates/:id/edit',
                name: 'admin.quotation-templates.edit',
                component: () => import('../pages/admin/quotations/TemplateEdit.vue'),
                meta: { permission: 'manage_quotations' }
            },
            // Projects Module
            {
                path: 'projects',
                name: 'admin.projects.index',
                component: () => import('../pages/admin/projects/Index.vue'),
                meta: { permission: 'view_projects' }
            },
            {
                path: 'projects/create',
                name: 'admin.projects.create',
                component: () => import('../pages/admin/projects/Create.vue'),
                meta: { permission: 'manage_projects' }
            },
            {
                path: 'projects/:id',
                name: 'admin.projects.show',
                component: () => import('../pages/admin/projects/Show.vue'),
                meta: { permission: 'view_projects' }
            },
            {
                path: 'projects/:id/edit',
                name: 'admin.projects.edit',
                component: () => import('../pages/admin/projects/Edit.vue'),
                meta: { permission: 'manage_projects' }
            },
            // Tickets Module
            {
                path: 'tickets',
                name: 'admin.tickets.index',
                component: () => import('../pages/admin/tickets/Index.vue'),
                meta: { permission: 'access_tickets' }
            },
            {
                path: 'tickets/:uuid',
                name: 'admin.tickets.show',
                component: () => import('../pages/admin/tickets/Index.vue'),
                meta: { permission: 'access_tickets' }
            },
            // Companies Module
            {
                path: 'companies',
                name: 'admin.companies.index',
                component: () => import('../pages/admin/companies/Index.vue'),
                meta: { permission: 'manage_companies' }
            },
            {
                path: 'companies/create',
                name: 'admin.companies.create',
                component: () => import('../pages/admin/companies/Create.vue'),
                meta: { permission: 'manage_companies' }
            },
            {
                path: 'companies/:id/edit',
                name: 'admin.companies.edit',
                component: () => import('../pages/admin/companies/Edit.vue'),
                meta: { permission: 'manage_companies' }
            },
            // Providers Module (super_admin only)
            {
                path: 'providers',
                name: 'admin.providers.index',
                component: () => import('../pages/admin/providers/Index.vue'),
                meta: { roles: ['super_admin'] }
            },
            {
                path: 'providers/onboard',
                name: 'admin.providers.onboard',
                component: () => import('../pages/admin/providers/Onboard.vue'),
                meta: { roles: ['super_admin'] }
            },
            {
                path: 'providers/:id/edit',
                name: 'admin.providers.edit',
                component: () => import('../pages/admin/providers/Edit.vue'),
                meta: { roles: ['super_admin'] }
            },
            // Activity Logs
            {
                path: 'activity',
                name: 'admin.activity.index',
                component: () => import('../pages/admin/activity/Index.vue')
            },
            // Settings
            {
                path: 'settings',
                name: 'admin.settings.index',
                component: () => import('../pages/admin/settings/Index.vue'),
                meta: { permission: 'manage_notifications' }
            }
        ]
    },
    // Profile (shared — admin + handyman)
    {
        path: '/profile',
        component: () => import('../layouts/AdminLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'profile',
                component: () => import('../pages/profile/Index.vue')
            }
        ]
    },
    {
        path: '/handyman',
        component: () => import('../layouts/HandymanLayout.vue'),
        meta: { requiresAuth: true, roles: ['handyman', 'vendor'] },
        children: [
            {
                path: 'dashboard',
                name: 'handyman.dashboard',
                component: () => import('../pages/handyman/Dashboard.vue')
            },
            {
                path: 'orders',
                name: 'handyman.orders.index',
                component: () => import('../pages/handyman/orders/Index.vue')
            },
            {
                path: 'orders/:orderNumber',
                name: 'handyman.orders.show',
                component: () => import('../pages/handyman/orders/Show.vue')
            },
            {
                path: 'projects/:id',
                name: 'handyman.projects.show',
                component: () => import('../pages/handyman/projects/Show.vue')
            },
            {
                path: 'leave',
                name: 'handyman.leave.index',
                component: () => import('../pages/handyman/leave/Index.vue')
            },
            {
                path: 'profile',
                name: 'handyman.profile',
                component: () => import('../pages/profile/Index.vue')
            }
        ]
    },
    // Customer Module
    {
        path: '/customer',
        component: () => import('../layouts/CustomerLayout.vue'),
        meta: { requiresAuth: true, roles: ['customer'] },
        children: [
            {
                path: 'dashboard',
                name: 'customer.orders.index',
                component: () => import('../pages/customer/orders/Index.vue')
            },
            {
                path: 'orders/create',
                name: 'customer.orders.create',
                component: () => import('../pages/customer/orders/Create.vue')
            },
            {
                path: 'orders/history',
                name: 'customer.orders.history',
                component: () => import('../pages/customer/orders/History.vue')
            },
            {
                path: 'orders/:orderNumber',
                name: 'customer.orders.show',
                component: () => import('../pages/customer/orders/Show.vue')
            },
            {
                path: 'orders/:orderNumber/invoice',
                name: 'customer.orders.invoice',
                component: () => import('../pages/customer/orders/Invoice.vue')
            },
            {
                path: 'properties',
                name: 'customer.properties',
                component: () => import('../pages/customer/properties/Index.vue')
            },
            {
                path: 'properties/create',
                name: 'customer.properties.create',
                component: () => import('../pages/customer/properties/Create.vue')
            },
            {
                path: 'properties/:id/edit',
                name: 'customer.properties.edit',
                component: () => import('../pages/customer/properties/Edit.vue')
            },
            {
                path: 'properties/:id/scheduled-services',
                name: 'customer.properties.scheduled-services',
                component: () => import('../pages/customer/properties/ScheduledServices.vue')
            },
            {
                path: 'properties/:id/order-history',
                name: 'customer.properties.order-history',
                component: () => import('../pages/customer/properties/OrderHistory.vue')
            },
            {
                path: 'scheduled-services',
                name: 'customer.scheduled-services',
                component: () => import('../pages/customer/scheduled-services/Index.vue')
            },
            {
                path: 'activity',
                name: 'customer.activity',
                component: () => import('../pages/customer/activity/Index.vue')
            },
            {
                path: 'profile',
                name: 'customer.profile',
                component: () => import('../pages/profile/Index.vue')
            }
        ]
    },
    // Catch all 404
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('../pages/NotFound.vue')
    }
];

const router = createRouter({
    history: createWebHistory('/'),
    routes,
});

// Navigation Guard
router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();

    // Check if the user is authenticated (from store or by fetching)
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        try {
            await authStore.fetchUser();
            if (authStore.isAuthenticated) {
                // Check for role permissions if needed
                if (to.meta.roles) {
                    const userRoles = authStore.user?.role_names || authStore.user?.roles || [];
                    const hasRole = to.meta.roles.some(role => userRoles.includes(role));
                    if (!hasRole) {
                        // Redirect to unauthorized or home. 
                        // For now, if role mismatch, maybe redirect to login or show error.
                        // But to be safe, let's redirect to login for now.
                        next({ name: 'login' });
                        return;
                    }
                }
                next();
            } else {
                next({ name: 'login' });
            }
        } catch (error) {
            next({ name: 'login' });
        }
    } else if (to.meta.guest) {
        // For guest routes, check if user is already logged in
        if (!authStore.isAuthenticated) {
            try {
                await authStore.fetchUser();
            } catch {
                // Not logged in — allow access to guest page
            }
        }

        if (authStore.isAuthenticated) {
            // Redirect logged-in users to their dashboard
            const userRoles = authStore.user?.role_names || authStore.user?.roles || [];
            if (userRoles.includes('handyman') || userRoles.includes('vendor')) {
                next({ name: 'handyman.dashboard' });
            } else if (userRoles.includes('customer')) {
                next({ name: 'customer.orders.index' });
            } else {
                next({ name: 'admin.dashboard' });
            }
        } else {
            next();
        }
    } else {
        // User is authenticated or route doesn't require auth
        if (to.meta.roles && authStore.isAuthenticated) {
            const userRoles = authStore.user?.role_names || authStore.user?.roles || [];
            const hasRole = to.meta.roles.some(role => userRoles.includes(role));
            if (!hasRole) {
                // Redirect to unauthorized or home
                // If invalid role, force logout or redirect?
                // Let's redirect to login for safety
                next({ name: 'login' });
                return;
            }
            next();
        } else {
            next();
        }
    }
});

export default router;
